<template>
    <section :id="sectionId" class="mb-10">
        <h2 class="text-xl font-semibold mb-2">MyAffiliates</h2>
        <Accordion :multiple="true" v-model:activeIndex="accordionActive">
            <AccordionTab header="Setting up tracking links">
                <div class="text-sm leading-relaxed">
                    <p class="mb-2">
                        Tracking links are created from the affiliate panel's <strong>Marketing</strong> tab. Every
                        publisher / site pairing needs its own link so we can attribute clicks and conversions correctly.
                    </p>

                    <ol class="list-decimal ml-6 mb-3 space-y-1">
                        <li>Log in to the MyAffiliates admin panel.</li>
                        <li>Go to <strong>Marketing</strong>.</li>
                        <li>Click <strong>Manage Campaigns</strong> on the campaign you want to add a link under.</li>
                        <li>Fill in the campaign / geo / creative details and save.</li>
                        <li>Copy the generated tracking URL — it looks like<br />
                            <code class="bg-black/40 px-1 py-0.5 rounded">https://record.staraffiliates.io/_&lt;hash&gt;/&lt;mediaItemId&gt;/</code>
                            <br />
                            Append <code class="bg-black/40 px-1 py-0.5 rounded">?s2s.req_id={clickid}</code> when linking it
                            to a publisher (the platform receives the clickid on postback).
                        </li>
                        <li>Paste the enriched URL into the pending SiteBrandLink in AffiliationSpace under
                            <strong>Advertisers → Requested</strong> and approve it. The <code>internalNote</code> field
                            auto-fills from the trailing numeric segment of the URL — that's the media-item ID.</li>
                    </ol>

                    <p class="mb-2 font-medium">Screens from the MyAffiliates panel</p>
                    <div class="flex flex-col gap-3">
                        <img src="https://documentation-screenshots-1.s3.eu-central-1.amazonaws.com/affiliatesystems/myAffiliates/adding_link_p1.png"
                             alt="MyAffiliates — Marketing tab" class="rounded" style="width: 100%; max-width: 1000px; height: auto;" />
                        <img src="https://documentation-screenshots-1.s3.eu-central-1.amazonaws.com/affiliatesystems/myAffiliates/adding_link_p2.png"
                             alt="MyAffiliates — Manage Campaigns" class="rounded" style="width: 100%; max-width: 1000px; height: auto;" />
                        <img src="https://documentation-screenshots-1.s3.eu-central-1.amazonaws.com/affiliatesystems/myAffiliates/adding_link_p3.png"
                             alt="MyAffiliates — Campaign details" class="rounded" style="width: 100%; max-width: 1000px; height: auto;" />
                        <img src="https://documentation-screenshots-1.s3.eu-central-1.amazonaws.com/affiliatesystems/myAffiliates/adding_link_p4.png"
                             alt="MyAffiliates — Copy tracking URL" class="rounded" style="width: 100%; max-width: 1000px; height: auto;" />
                    </div>

                    <div class="mt-4 p-3 rounded border border-amber-400/50 bg-amber-50/10 text-xs">
                        <p class="font-semibold mb-1">Notes</p>
                        <ul class="list-disc ml-4 space-y-0.5">
                            <li>The trailing numeric segment of the URL <em>is</em> the media-item ID — save this as
                                <code>internalNote</code> on the SiteBrandLink.</li>
                            <li>Each SiteBrandLink under the same <code>spaceAdvertiserLogin</code> must have a unique
                                <code>internalNote</code>. The guard on <code>updateSiteBrandLink</code> enforces this.</li>
                            <li>Enrichment (adding <code>?s2s.req_id={clickid}</code>) can also be done automatically by
                                the affiliate-link enricher in <code>src/utils/affiliate-link-enricher.js</code> when
                                approving a link via the UI.</li>
                        </ul>
                    </div>
                </div>
            </AccordionTab>

            <AccordionTab header="Adding the link in AffiliationSpace">
                <div class="text-sm leading-relaxed">
                    <p class="mb-3">
                        Once you've generated the tracking URL in the MyAffiliates panel (or been sent one by the
                        operator), attach it to the publisher's pending request inside AffiliationSpace.
                    </p>

                    <ol class="list-decimal ml-6 mb-4 space-y-1">
                        <li>Go to <strong>Advertisers</strong> in the sidebar.</li>
                        <li>Switch the filter to <strong>All</strong> (or <strong>Show Only Requested</strong>) and expand
                            the brand row — you'll see one line per pending SiteBrandLink.</li>
                        <li>For the row you want to approve, paste the full tracking URL into <strong>Affiliate Link</strong>.</li>
                        <li>Enter the <strong>Internal Note</strong> — this <em>must</em> be the numeric media-item ID
                            (the trailing segment of the tracking URL, e.g. <code>19</code> for
                            <code>…/19/</code>).</li>
                        <li>Save — the row's Link status flips to <span class="text-green-400 font-semibold">linked</span>.</li>
                    </ol>

                    <div class="mb-4">
                        <img :src="addingLinkScreenshot"
                             alt="AffiliationSpace — pending SiteBrandLink row with Internal Note + Affiliate Link"
                             class="rounded border border-gray-700"
                             style="width: 100%; max-width: 1200px; height: auto;" />
                        <p class="text-xs text-gray-400 mt-1">
                            Example: <em>Let's Jackpot</em> pending link — Internal Note <code>19</code> matches the
                            trailing segment of the affiliate URL; Link status shows <span class="text-green-400">linked</span> once saved.
                        </p>
                    </div>

                    <div class="p-3 rounded border border-red-500/60 bg-red-950/30 text-xs mb-4">
                        <p class="font-semibold mb-1">⚠ The Internal Note ID must be unique per SpaceAdvertiserLogin</p>
                        <p>
                            If two publishers end up with the same media-item ID under the same partner login, our
                            tracking postbacks + earnings will attribute events to the wrong publisher. The backend
                            guards against this at save time — <code>services/site-link-service.js → updateSiteBrandLink</code>
                            throws <code>DuplicateResourceError</code> if a non-deleted SiteBrandLink already exists with
                            the same <code>(spaceAdvertiserLoginId, internalNote)</code> pair. You'll see a clear error
                            in the UI; pick a different ID (or check whether the request should have been reassigned
                            rather than newly-created).
                        </p>
                    </div>

                    <div class="p-3 rounded border border-amber-400/50 bg-amber-50/10 text-xs mb-4">
                        <p class="font-semibold mb-1">Even when the operator sends you a pre-made link…</p>
                        <p>
                            …you <em>still</em> need to add the media-item ID to <strong>Internal Note</strong>
                            manually. The link on its own is not enough — the ID is what our postbacks / campaign
                            mapping / invoice attribution key off. Skipping it is what caused past
                            &quot;campaign exists but no earnings show up&quot; incidents (e.g. BigClash <code>mid=2014513</code>
                            unmapped for months).
                        </p>
                    </div>

                    <div class="p-3 rounded border border-blue-400/50 bg-blue-950/30 text-xs">
                        <p class="font-semibold mb-1">Link format validation</p>
                        <p>
                            The affiliate-link enricher (<code>src/utils/affiliate-link-enricher.js</code>) validates the
                            URL shape on paste and auto-extracts the trailing numeric ID into Internal Note when it
                            matches the expected MyAffiliates pattern
                            (<code>…/&lt;hash&gt;/&lt;mediaItemId&gt;/</code>). If the format is off, Internal Note stays
                            empty and you'll need to fix the URL before saving.
                        </p>
                    </div>
                </div>
            </AccordionTab>
        </Accordion>
    </section>
</template>

<script setup>
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import { useHelpSection } from './useHelpSection';
import addingLinkScreenshot from '@/assets/docs/adding-link-in-affiliationspace.png';
const { sectionId, accordionActive } = useHelpSection('links-myaffiliates');
</script>
